import { useState } from "react";
import { toast } from "sonner";
import { api, fileToDataUrl } from "@/lib/api";
import { colors as C } from "@/lib/theme";
import { Ion } from "@/components/Ion";
import { FormLabel, Sublabel, Help, Input, BtnPrimary, Chip, ChipRow, FilePicker, IconAction, DeleteBtn, RefreshBtn } from "@/components/AdminParts";

export const LASER_MODELS = ["300W", "500W", "1000W", "2000W"];
const toggle = (arr, m) => (arr.includes(m) ? arr.filter((x) => x !== m) : [...arr, m]);
const Tag = ({ children, model }) => (
  <span className="rounded-full border px-2 py-[3px] text-[9px] font-extrabold tracking-[1px]" style={model ? { background: "rgba(76,204,6,0.1)", borderColor: C.accent, color: C.accent } : { background: C.surface, borderColor: C.border, color: C.textSecondary }}>{children}</span>
);
export const EditBanner = ({ editing, text, onCancel, testId }) => (
  <div className="my-2 flex items-center gap-2 rounded-lg border p-2" style={{ background: C.surface, borderColor: C.border }}>
    <Ion name={editing ? "create" : "add-circle-outline"} size={16} color={editing ? C.warning : C.accent} />
    <span className="flex-1 text-[13px] font-bold" style={editing ? { color: C.warning } : undefined}>{text}</span>
    {onCancel && <button data-testid={testId} onClick={onCancel} className="text-[11px] font-extrabold tracking-[1px]" style={{ color: C.warning }}>CANCEL</button>}
  </div>
);

export function KbTab({ list, reload }) {
  const [editId, setEditId] = useState(null);
  const [d, setD] = useState({ title: "", content: "", tags: "", models: [] });
  const [busy, setBusy] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [pdfModels, setPdfModels] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const cancel = () => { setEditId(null); setD({ title: "", content: "", tags: "", models: [] }); };
  const save = async () => {
    if (!d.title.trim() || !d.content.trim()) return;
    setBusy(true);
    try {
      const body = { title: d.title, content: d.content, tags: d.tags.split(",").map((t) => t.trim()).filter(Boolean), laser_models: d.models };
      await api(editId ? `/kb/${editId}` : "/kb", { method: editId ? "PATCH" : "POST", body });
      toast.success(editId ? "KB entry updated." : "KB entry created. The AI Assistant will use it on next chat.");
      cancel(); reload();
    } catch (e) { toast.error(e.message || "Failed"); } finally { setBusy(false); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Delete KB entry "${e.title}"?`)) return;
    try { await api(`/kb/${e.id}`, { method: "DELETE" }); reload(); } catch (err) { toast.error(err.message || "Delete failed"); }
  };
  const parsePdf = async () => {
    if (!pdf) return toast.error("Pick a PDF first.");
    setPdfBusy(true);
    try {
      const r = await api("/kb/upload-pdf", { method: "POST", body: { file_name: pdf.name || "knowledge.pdf", pdf_base64: await fileToDataUrl(pdf), laser_models: pdfModels, tags: [] } });
      toast.success(`Parsed ${r.pages} pages → created ${r.chunks_created} KB entries (${Number(r.characters).toLocaleString()} characters).`);
      setPdf(null); setPdfModels([]); reload();
    } catch (e) { toast.error(e.message || "Upload failed"); } finally { setPdfBusy(false); }
  };
  return (
    <div data-testid="console-kb-tab">
      <FormLabel>AI KNOWLEDGE BASE</FormLabel>
      <Help>Train the AI Assistant. Add settings, troubleshooting recipes, or upload PDFs that will be parsed and chunked automatically. The AI uses this in every chat.</Help>
      <EditBanner editing={!!editId} text={editId ? `EDITING: ${d.title || "(untitled)"}` : "1 — Add a text snippet"} onCancel={editId ? cancel : null} testId="kb-cancel-edit" />
      <Input data-testid="kb-title" placeholder="Title (e.g. Rust on cast iron — 1000W settings)" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} />
      <Input data-testid="kb-content" multiline rows={6} placeholder={"Body — settings, steps, observations.\nExample: 'Use 50% power, 1500mm/s scan, 0.05mm line spacing, 2 passes…'"} value={d.content} onChange={(e) => setD({ ...d, content: e.target.value })} />
      <Input data-testid="kb-tags" placeholder="Tags (comma-separated, optional)" value={d.tags} onChange={(e) => setD({ ...d, tags: e.target.value })} />
      <Sublabel>Applicable laser models (leave empty = all)</Sublabel>
      <ChipRow>{LASER_MODELS.map((m) => <Chip key={m} active={d.models.includes(m)} data-testid={`kb-model-${m}`} onClick={() => setD({ ...d, models: toggle(d.models, m) })}>{m}</Chip>)}</ChipRow>
      <BtnPrimary data-testid="kb-save" busy={busy} onClick={save}>{busy ? "SAVING…" : editId ? "UPDATE KB ENTRY" : "SAVE KB ENTRY"}</BtnPrimary>

      <Sublabel className="!mt-8">2 — Upload a PDF (server-side parse + chunk)</Sublabel>
      <Help>The server extracts text from each page and creates multiple KB entries automatically. Best for product manuals, settings tables, or technical bulletins.</Help>
      <FilePicker accept="application/pdf" testId="kb-pdf-pick" icon="document-attach-outline" label={pdf ? `Selected: ${pdf.name}` : "Choose PDF…"} selected={!!pdf} onClear={() => setPdf(null)} onPick={setPdf} />
      <Sublabel>Tag uploaded chunks for these lasers (optional)</Sublabel>
      <ChipRow>{LASER_MODELS.map((m) => <Chip key={m} active={pdfModels.includes(m)} data-testid={`kb-pdf-model-${m}`} onClick={() => setPdfModels(toggle(pdfModels, m))}>{m}</Chip>)}</ChipRow>
      <BtnPrimary data-testid="kb-pdf-upload" busy={pdfBusy} disabled={!pdf} onClick={parsePdf}>{pdfBusy ? "PARSING…" : "PARSE & ADD TO KB"}</BtnPrimary>

      <FormLabel className="mt-8"><span data-testid="kb-count">KB ENTRIES ({list.length})</span></FormLabel>
      {list.length === 0 && <Help>No KB entries yet. Add settings or upload a PDF above to teach the AI.</Help>}
      {list.map((e) => (
        <div key={e.id} className="mb-2 flex items-start gap-2 rounded-[14px] border p-4" style={{ background: C.card, borderColor: C.border }} data-testid={`kb-row-${e.id}`}>
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-sm font-bold">{e.title}</p>
            <p className="mt-0.5 line-clamp-2 text-xs" style={{ color: C.textSecondary }}>{e.content.slice(0, 140)}{e.content.length > 140 ? "…" : ""}</p>
            {(e.laser_models?.length || e.tags?.length) ? <div className="mt-1.5 flex flex-wrap gap-1">{(e.laser_models || []).map((m) => <Tag key={m} model>{m}</Tag>)}{(e.tags || []).map((t) => <Tag key={t}>#{t}</Tag>)}</div> : null}
          </div>
          <IconAction icon="create-outline" title="Edit" data-testid={`kb-edit-${e.id}`} onClick={() => { setEditId(e.id); setD({ title: e.title, content: e.content, tags: (e.tags || []).join(", "), models: e.laser_models || [] }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          <DeleteBtn data-testid={`kb-delete-${e.id}`} onClick={() => remove(e)} />
        </div>
      ))}
    </div>
  );
}

const Stat = ({ value, label, color, testId }) => (
  <div className="min-w-[47%] flex-1 basis-[47%] rounded-[14px] border p-4" style={{ background: C.card, borderColor: color || C.border }} data-testid={testId}>
    <p className="flex items-center gap-1.5 text-[22px] font-extrabold" style={{ color: color || C.text }}>{value}</p>
    <p className="mt-1 text-[10px] font-bold tracking-[1.5px]" style={{ color: C.textSecondary }}>{label}</p>
  </div>
);

export function InsightsTab({ stats, feedback, busy, reload }) {
  return (
    <div data-testid="console-insights-tab">
      <FormLabel>AI INSIGHTS</FormLabel>
      <Help>Snapshot of AI Assistant usage across all franchisees, plus thumbs-up/down feedback so you can spot weak answers and improve the KB.</Help>
      <div className="mt-2 flex flex-wrap gap-2">
        <Stat value={stats?.total_chats ?? "—"} label="QUESTIONS ASKED" testId="insights-total-chats" />
        <Stat value={stats?.total_messages ?? "—"} label="TOTAL MESSAGES" testId="insights-total-messages" />
        <Stat value={<><Ion name="thumbs-up" size={18} color={C.accent} />{stats?.thumbs_up ?? 0}</>} label="HELPFUL" color={C.accent} testId="insights-thumbs-up" />
        <Stat value={<><Ion name="thumbs-down" size={18} color={C.error} />{stats?.thumbs_down ?? 0}</>} label="NOT HELPFUL" color={C.error} testId="insights-thumbs-down" />
        <Stat value={stats?.kb_entries ?? "—"} label="KB ENTRIES" testId="insights-kb-entries" />
      </div>
      <div className="mb-2 mt-6 flex items-center justify-between">
        <FormLabel className="!mb-0">RECENT FEEDBACK ({feedback.length})</FormLabel>
        <RefreshBtn data-testid="insights-refresh" busy={busy} onClick={reload} />
      </div>
      {feedback.length === 0 && <Help>No feedback yet. Franchisees can rate replies in the chat with thumbs up / down — entries appear here.</Help>}
      {feedback.map((fb) => {
        const up = fb.rating === "up", col = up ? C.accent : C.error;
        return (
          <div key={fb.id} className="mb-2 rounded-[14px] border p-4" style={{ background: C.card, borderColor: up ? "rgba(76,204,6,0.4)" : "rgba(255,80,80,0.4)" }} data-testid={`insights-fb-${fb.id}`}>
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 rounded-full border px-2 py-[3px] text-[10px] font-extrabold tracking-[1px]" style={{ borderColor: col, color: col }}><Ion name={up ? "thumbs-up" : "thumbs-down"} size={11} color={col} />{up ? "HELPFUL" : "NOT HELPFUL"}</span>
              <span className="flex-1 truncate text-[10px]" style={{ color: C.textSecondary }}>{fb.user_email}{fb.laser_model ? ` • ${fb.laser_model}` : ""}{fb.created_at ? ` • ${new Date(fb.created_at).toLocaleDateString()}` : ""}</span>
            </div>
            {fb.question && <><p className="mt-1 text-[10px] font-bold tracking-[1px]" style={{ color: C.accent }}>Q:</p><p className="mt-0.5 line-clamp-3 text-[13px] leading-[18px]">{fb.question}</p></>}
            {fb.answer && <><p className="mt-1.5 text-[10px] font-bold tracking-[1px]" style={{ color: C.accent }}>A:</p><p className="mt-0.5 line-clamp-6 whitespace-pre-line text-[13px] leading-[18px]">{fb.answer}</p></>}
          </div>
        );
      })}
    </div>
  );
}
